import { parseSearchTermTableRowKey } from '@/utils/workspaceSearchTermMetrics';
import type {
  EntityCrossFilter,
  EntityGraph,
  PropagatedConstraints,
  WorkspaceResolutionContext,
} from './types';
import { campaignKeyForAdGroup } from './buildEntityGraph';
import { propagateEntityConstraints } from './propagateEntityConstraints';
import {
  cloneCrossFilter,
  hasOnlyFacetPins,
  isCrossFilterEmpty,
  normalizeCrossFilter,
} from './toggleFilter';

function shortAdProduct(adProduct: string): string {
  const u = adProduct.toUpperCase();
  if (u === 'SB' || u === 'SPONSORED_BRANDS' || u.includes('BRAND')) return 'SB';
  if (u === 'SD' || u === 'SPONSORED_DISPLAY' || u.includes('DISPLAY')) return 'SD';
  return 'SP';
}

function intersectSets(sets: Set<string>[]): Set<string> {
  if (sets.length === 0) return new Set();
  let out = new Set(sets[0]);
  for (let i = 1; i < sets.length; i += 1) {
    const next = new Set<string>();
    for (const value of out) {
      if (sets[i].has(value)) next.add(value);
    }
    out = next;
  }
  return out;
}

function unionSets(sets: Set<string>[]): Set<string> {
  const out = new Set<string>();
  for (const set of sets) {
    for (const value of set) out.add(value);
  }
  return out;
}

function emptyConflict(): PropagatedConstraints {
  return {
    portfolioIds: new Set(),
    campaignKeys: new Set(),
    adGroupIds: new Set(),
    keywordIds: new Set(),
    conflict: true,
  };
}

function fullGraph(graph: EntityGraph): PropagatedConstraints {
  return {
    portfolioIds: new Set(graph.portfolios.map((p) => p.portfolioId)),
    campaignKeys: new Set(graph.campaigns.map((c) => c.campaignKey)),
    adGroupIds: new Set(graph.adGroups.map((a) => a.adGroupId)),
    keywordIds: new Set(graph.indices.allKeywordIds),
    conflict: false,
  };
}

/** Map SP/SB/SD (or report ids) → campaign graph keys. */
export function campaignKeysForAdProducts(
  graph: EntityGraph,
  adProducts: string[]
): Set<string> {
  const wanted = new Set(
    adProducts.map((p) => {
      const u = p.toUpperCase();
      if (u === 'SPONSORED_PRODUCTS' || u === 'SPONSORED PRODUCTS') return 'SP';
      if (u === 'SPONSORED_BRANDS' || u === 'SPONSORED BRANDS') return 'SB';
      if (u === 'SPONSORED_DISPLAY' || u === 'SPONSORED DISPLAY') return 'SD';
      return u;
    })
  );
  const out = new Set<string>();
  for (const campaign of graph.campaigns) {
    if (wanted.has(campaign.adProduct)) out.add(campaign.campaignKey);
  }
  return out;
}

/** Catalog-based match-type → campaign keys (EXACT/PHRASE/BROAD from keywords). */
export function campaignKeysForMatchTypesFromCatalog(
  graph: EntityGraph,
  matchTypes: string[]
): Set<string> {
  const wanted = new Set(matchTypes.map((m) => m.toLowerCase()));
  const keywordMatchWanted = new Set<string>(
    [...wanted].filter((m) => m === 'exact' || m === 'phrase' || m === 'broad')
  );
  const out = new Set<string>();

  if (keywordMatchWanted.size > 0) {
    for (const row of graph.keywords) {
      const mt = row.match_type.toLowerCase();
      if (!keywordMatchWanted.has(mt)) continue;
      const pins = graph.indices.keywordPinsByGroupKey.get(row.groupKey) ?? [];
      for (const pin of pins) {
        out.add(campaignKeyForAdGroup(pin.adProduct, pin.campaignId));
      }
    }
  }

  // Non-keyword buckets need report context; leave empty here so caller can AND with report map.
  return out;
}

function keysFromBucketMap(
  map: Map<string, Set<string>> | undefined,
  ids: string[]
): Set<string> | null {
  if (!ids.length) return null;
  if (!map) return new Set();
  const parts: Set<string>[] = [];
  for (const id of ids) {
    parts.push(map.get(id) ?? new Set());
  }
  return unionSets(parts);
}

function campaignKeysForAsins(
  graph: EntityGraph,
  asins: string[],
  catalog: WorkspaceResolutionContext['productAdCatalog']
): Set<string> {
  if (!catalog?.length) return new Set();
  const wanted = new Set(asins.map((a) => a.toUpperCase()));
  const out = new Set<string>();
  for (const row of catalog) {
    if (!wanted.has(row.asin.toUpperCase())) continue;
    const product = row.adProduct === 'SB' || row.adProduct === 'SD' ? row.adProduct : 'SP';
    // Prefer graph key when campaign exists.
    const match = graph.campaigns.find(
      (c) => c.campaignId === row.campaignId && c.adProduct === product
    );
    if (match) out.add(match.campaignKey);
    else out.add(campaignKeyForAdGroup(product, row.campaignId));
  }
  return out;
}

type ExpandedPins = {
  campaignKeys: Set<string> | null;
  adGroupIds: Set<string> | null;
  keywordIds: Set<string> | null;
  conflict: boolean;
};

/**
 * Expand attribute / search / product pins into campaign (and optional ad-group / keyword) sets.
 * Returns null campaignKeys when no attribute pins contribute.
 */
export function expandAttributePins(
  graph: EntityGraph,
  filter: EntityCrossFilter,
  ctx: WorkspaceResolutionContext = {}
): ExpandedPins {
  const f = normalizeCrossFilter(filter);
  const campaignSets: Set<string>[] = [];
  let adGroupIds: Set<string> | null = null;
  let keywordIds: Set<string> | null = null;

  if (f.adProducts.length) {
    campaignSets.push(campaignKeysForAdProducts(graph, f.adProducts));
  }

  if (f.targetingTypes.length) {
    const fromReport = keysFromBucketMap(ctx.campaignKeysByTargetingType, f.targetingTypes);
    campaignSets.push(fromReport ?? new Set());
  }

  if (f.matchTypes.length) {
    const fromReport = keysFromBucketMap(ctx.campaignKeysByMatchType, f.matchTypes);
    const fromCatalog = campaignKeysForMatchTypesFromCatalog(graph, f.matchTypes);
    // Prefer union of catalog + report so EXACT/PHRASE/BROAD work without reports.
    if (fromReport && fromReport.size > 0) {
      campaignSets.push(unionSets([fromReport, fromCatalog]));
    } else if (fromCatalog.size > 0) {
      campaignSets.push(fromCatalog);
    } else {
      campaignSets.push(fromReport ?? new Set());
    }
  }

  if (f.placements.length) {
    const fromReport = keysFromBucketMap(ctx.campaignKeysByPlacement, f.placements);
    campaignSets.push(fromReport ?? new Set());
  }

  if (f.asins.length) {
    campaignSets.push(campaignKeysForAsins(graph, f.asins, ctx.productAdCatalog));
  }

  if (f.searchTermKeys.length) {
    const ck = new Set<string>();
    const ag = new Set<string>();
    const kw = new Set<string>();
    for (const key of f.searchTermKeys) {
      let row = ctx.searchTermsByKey?.get(key);
      if (!row) {
        const parsed = parseSearchTermTableRowKey(key);
        if (parsed?.campaignId) {
          row = {
            campaignId: parsed.campaignId,
            adGroupId: parsed.adGroupId || '',
            adProduct: 'sponsored_products',
          };
        }
      }
      if (!row) continue;
      ck.add(campaignKeyForAdGroup(shortAdProduct(row.adProduct), row.campaignId));
      if (row.adGroupId) ag.add(row.adGroupId);
      if (row.keywordId) kw.add(row.keywordId);
    }
    campaignSets.push(ck);
    if (ag.size) adGroupIds = ag;
    if (kw.size) keywordIds = kw;
  }

  if (f.adProductRowKeys.length) {
    const ck = new Set<string>();
    for (const key of f.adProductRowKeys) {
      const row = ctx.adProductRowsByKey?.get(key);
      if (!row) continue;
      const product = row.adProduct === 'SB' || row.adProduct === 'SD' ? row.adProduct : 'SP';
      ck.add(campaignKeyForAdGroup(product, row.campaignId));
    }
    campaignSets.push(ck);
  }

  if (campaignSets.length === 0) {
    return { campaignKeys: null, adGroupIds, keywordIds, conflict: false };
  }

  const campaignKeys = intersectSets(campaignSets);
  return {
    campaignKeys,
    adGroupIds,
    keywordIds,
    conflict: campaignKeys.size === 0,
  };
}

/**
 * Resolve full Workspace filter (entity + attribute pins) into hierarchy constraints.
 */
export function resolveWorkspaceConstraints(
  graph: EntityGraph,
  filter: EntityCrossFilter,
  ctx: WorkspaceResolutionContext = {}
): PropagatedConstraints {
  const f = normalizeCrossFilter(filter);

  if (isCrossFilterEmpty(f)) {
    return fullGraph(graph);
  }

  // Audience-only pins do not constrain the entity hierarchy.
  if (hasOnlyFacetPins(f)) {
    return fullGraph(graph);
  }

  const expanded = expandAttributePins(graph, f, ctx);
  if (expanded.conflict) {
    return emptyConflict();
  }

  const working = cloneCrossFilter(f);
  // Strip attribute dims — propagate only uses entity pins.
  working.adProducts = [];
  working.targetingTypes = [];
  working.matchTypes = [];
  working.placements = [];
  working.audienceSegments = [];
  working.searchTermKeys = [];
  working.asins = [];
  working.adProductRowKeys = [];

  if (expanded.campaignKeys) {
    if (working.campaignKeys.length) {
      const pinned = new Set(working.campaignKeys);
      working.campaignKeys = [...expanded.campaignKeys].filter((k) => pinned.has(k));
      if (working.campaignKeys.length === 0) return emptyConflict();
    } else {
      working.campaignKeys = [...expanded.campaignKeys];
    }
  }

  if (expanded.adGroupIds) {
    if (working.adGroupIds.length) {
      const pinned = new Set(working.adGroupIds);
      working.adGroupIds = [...expanded.adGroupIds].filter((id) => pinned.has(id));
      if (working.adGroupIds.length === 0) return emptyConflict();
    } else {
      working.adGroupIds = [...expanded.adGroupIds];
    }
  }

  if (expanded.keywordIds) {
    if (working.keywordIds.length) {
      const pinned = new Set(working.keywordIds);
      working.keywordIds = [...expanded.keywordIds].filter((id) => pinned.has(id));
      if (working.keywordIds.length === 0) return emptyConflict();
    } else {
      working.keywordIds = [...expanded.keywordIds];
    }
  }

  return propagateEntityConstraints(graph, working);
}
